<template>
  <div class="tab-content">
    <div class="financial-grid">
      <!-- Summary Cards -->
      <div class="financial-card">
        <div class="financial-card-header">
          <h4 class="title is-5">Revenue Summary</h4>
        </div>
        <div class="financial-stats-row">
          <div class="financial-stat-item">
            <div class="financial-stat-icon is-success">
              <span class="icon">
                <i class="fas fa-dollar-sign"></i>
              </span>
            </div>
            <div class="financial-stat-details">
              <p class="financial-stat-label">Monthly Revenue</p>
              <p class="financial-stat-value">${{ monthlyRevenue }}</p>
              <p class="financial-stat-meta">Current month</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Contracts -->
      <div class="financial-card">
        <div class="financial-card-header">
          <h4 class="title is-5">Contracts</h4>
        </div>
        <div class="financial-stats-column">
          <div class="financial-stat-item">
            <div class="financial-stat-icon is-primary">
              <span class="icon">
                <i class="fas fa-check-circle"></i>
              </span>
            </div>
            <div class="financial-stat-details">
              <p class="financial-stat-label">Active Contracts</p>
              <p class="financial-stat-value">{{ activeContracts }}</p>
              <p class="financial-stat-meta">Currently active</p>
            </div>
          </div>

          <div class="financial-stat-item">
            <div class="financial-stat-icon is-warning">
              <span class="icon">
                <i class="fas fa-clock"></i>
              </span>
            </div>
            <div class="financial-stat-details">
              <p class="financial-stat-label">Expiring Soon</p>
              <p class="financial-stat-value">{{ expiringContracts }}</p>
              <p class="financial-stat-meta">Within 30 days</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoices -->
      <div class="financial-card">
        <div class="financial-card-header">
          <h4 class="title is-5">Invoices</h4>
        </div>
        <div class="financial-stats-column">
          <div class="financial-stat-item">
            <div class="financial-stat-icon is-danger">
              <span class="icon">
                <i class="fas fa-exclamation-circle"></i>
              </span>
            </div>
            <div class="financial-stat-details">
              <p class="financial-stat-label">Overdue Invoices</p>
              <p class="financial-stat-value">{{ overdueInvoices }}</p>
              <p class="financial-stat-meta">Require attention</p>
            </div>
          </div>

          <div class="financial-stat-item">
            <div class="financial-stat-icon is-info">
              <span class="icon">
                <i class="fas fa-file-invoice-dollar"></i>
              </span>
            </div>
            <div class="financial-stat-details">
              <p class="financial-stat-label">Pending Invoices</p>
              <p class="financial-stat-value">{{ pendingInvoices }}</p>
              <p class="financial-stat-meta">Awaiting payment</p>
            </div>
          </div>
        </div>
      </div>

      <!-- System Status -->
      <div class="financial-card">
        <div class="financial-card-header">
          <h4 class="title is-5">System Status</h4>
        </div>
        <div class="status-items">
          <div class="status-item">
            <span class="status-dot status-active"></span>
            <span>API Server</span>
            <span class="status-badge is-success">Online</span>
          </div>
          <div class="status-item">
            <span class="status-dot status-active"></span>
            <span>Database</span>
            <span class="status-badge is-success">Connected</span>
          </div>
          <div class="status-item">
            <span class="status-dot status-active"></span>
            <span>Storage</span>
            <span class="status-badge is-success">Available</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Financial Charts Section -->
    <div class="dashboard-section">
      <div class="section-header">
        <h3 class="title is-5">Revenue Trends</h3>
        <div class="field has-addons">
          <p class="control">
            <span class="select is-small">
              <select
                v-model="financialPeriod"
                @change="$emit('update-financial-chart')"
              >
                <option value="1week">Last Week</option>
                <option value="1month">Last Month</option>
                <option value="3months">3 Months</option>
                <option value="6months">6 Months</option>
                <option value="1year">1 Year</option>
                <option value="custom">Custom</option>
              </select>
            </span>
          </p>
        </div>
      </div>
      <div v-if="financialPeriod === 'custom'" class="custom-date-range">
        <div class="field is-horizontal">
          <div class="field-label is-small">
            <label class="label">From:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="financialCustomStart"
                  type="date"
                  class="input is-small"
                  @change="$emit('update-financial-chart')"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-label is-small">
            <label class="label">To:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="financialCustomEnd"
                  type="date"
                  class="input is-small"
                  @change="$emit('update-financial-chart')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-wrapper">
        <LineChart
          v-if="financialChartData"
          :key="`financial-${financialChartKey}`"
          :chart-data="financialChartData"
          :options="lineChartOptions"
        />
        <div v-else class="no-data-message">
          <p>N/A - Data unavailable</p>
        </div>
      </div>
    </div>

    <div class="dashboard-section">
      <div class="section-header">
        <h3 class="title is-5">Package Distribution</h3>
        <div class="field has-addons">
          <p class="control">
            <span class="select is-small">
              <select
                v-model="packageMetric"
                @change="$emit('update-package-chart', packageMetric)"
              >
                <option value="quantity">By Quantity</option>
                <option value="revenue">By Revenue</option>
              </select>
            </span>
          </p>
        </div>
      </div>
      <div class="chart-wrapper">
        <PieChart
          v-if="packageDistributionData"
          :key="`packages-${packageChartKey}`"
          :chart-data="packageDistributionData"
          :options="pieChartOptions"
        />
        <div v-else class="no-data-message">
          <p>N/A - Data unavailable</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LineChart from "../LineChart.vue";
import PieChart from "../PieChart.vue";

export default {
  name: "FinancialTab",
  components: {
    LineChart,
    PieChart,
  },
  props: {
    monthlyRevenue: {
      type: [Number, String],
      required: true,
    },
    activeContracts: {
      type: Number,
      required: true,
    },
    expiringContracts: {
      type: Number,
      required: true,
    },
    overdueInvoices: {
      type: Number,
      required: true,
    },
    pendingInvoices: {
      type: Number,
      required: true,
    },
    financialChartData: {
      type: Object,
      default: null,
    },
    packageDistributionData: {
      type: Object,
      default: null,
    },
    financialChartKey: {
      type: Number,
      required: true,
    },
    packageChartKey: {
      type: Number,
      required: true,
    },
    lineChartOptions: {
      type: Object,
      required: true,
    },
    pieChartOptions: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      financialPeriod: "1month",
      financialCustomStart: null,
      financialCustomEnd: null,
      packageMetric: "quantity",
    };
  },
};
</script>

<style scoped>
.tab-content {
  padding: 1.5rem;
}

.financial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

@media (max-width: 1024px) {
  .financial-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.financial-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.financial-card-header {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.75rem;
  margin-bottom: 1rem;
}

.financial-card-header .title {
  margin-bottom: 0;
}

.financial-stats-row {
  display: flex;
  gap: 1rem;
}

.financial-stats-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.financial-stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.financial-stat-item:hover {
  background: #f0f0f0;
}

.financial-stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.financial-stat-icon.is-warning {
  background: rgba(255, 221, 87, 0.2);
  color: #ffdd57;
}

.financial-stat-icon.is-danger {
  background: rgba(241, 70, 104, 0.2);
  color: #f14668;
}

.financial-stat-icon.is-info {
  background: rgba(32, 156, 238, 0.2);
  color: #209cee;
}

.financial-stat-icon.is-success {
  background: rgba(72, 199, 116, 0.2);
  color: #48c774;
}

.financial-stat-icon.is-primary {
  background: rgba(50, 115, 220, 0.2);
  color: #3273dc;
}

.financial-stat-details {
  flex-grow: 1;
}

.financial-stat-label {
  font-size: 0.875rem;
  color: #7a7a7a;
  margin-bottom: 0.25rem;
}

.financial-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #363636;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.financial-stat-meta {
  font-size: 0.75rem;
  color: #b5b5b5;
}

.status-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
}

.status-item span:nth-child(2) {
  flex-grow: 1;
  font-weight: 500;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dbdbdb;
}

.status-dot.status-active {
  background: #48c774;
  box-shadow: 0 0 0 3px rgba(72, 199, 116, 0.2);
  animation: pulse 2s infinite;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.is-success {
  background: rgba(72, 199, 116, 0.2);
  color: #48c774;
}

.dashboard-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-header .title {
  margin-bottom: 0;
}

.chart-wrapper {
  background: white;
  padding: 1.5rem;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 400px;
}

.custom-date-range {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 4px;
}

.custom-date-range .field-label {
  flex-basis: 80px;
  flex-grow: 0;
  margin-right: 0.75rem;
}

.custom-date-range .label {
  text-align: right;
  font-size: 0.875rem;
  margin-bottom: 0;
}

.no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #7a7a7a;
  font-size: 1.1rem;
  font-weight: 500;
}

.custom-date-range .field-body {
  flex-grow: 5;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 3px rgba(72, 199, 116, 0.2);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(72, 199, 116, 0.1);
  }
}
</style>
